// Sidebar do app (shadcn Sidebar). Navegação principal: substitui a antiga top-nav.
// Grupos colapsáveis (Monitoramento, Relatórios, Gestão) + itens diretos (Dashboard, Discador).
// O rodapé é reativo à sessão (Better Auth): logado mostra usuário + "Sair"; senão "Entrar" (-> /auth).
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useRouterState, type LinkProps } from "@tanstack/react-router";
import { LayoutGroup, motion } from "motion/react";
import type { LucideIcon } from "lucide-react";
import {
  Building2,
  ChevronRight,
  ChevronsUpDown,
  Cookie,
  Download,
  LayoutDashboard,
  LogIn,
  LogOut,
  Moon,
  PhoneOutgoing,
  UserRound,
  Sun,
  Sparkles,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Kbd,
} from "@organizacaox/lai-design-system";
import { Search } from "@organizacaox/lai-design-system/icons";
import { logout, useActiveOrganization, useSession } from "@/lib/auth-client";
import { usePreferences, type Theme } from "./preferences-store.js";
import { LanguageSubmenu } from "./language.js";
import { AppBrandMark } from "./brand.js";
import {
  AUDITORIA,
  CONFIGURACOES,
  clickToCallNavGroup,
  filterNavGroup,
  FILA,
  FINANCEIRO,
  GESTAO,
  INTEGRACOES,
  JORNADA_LINK,
  manualDialNavLink,
  MEU_DESEMPENHO_LINK,
  MONITORAMENTO,
  navTranslationKey,
  RELATORIOS,
  showInNav,
  SUPORTE,
  type NavGroupDef,
  activeSubItem,
} from "./nav-config.js";
import { canAccess } from "./lib/access.js";
import { useClickToCallEnabled } from "./lib/click-to-call-enabled.js";
import { useManualDialEnabled } from "./lib/manual-dial-enabled.js";
import { useSupportEnabled } from "./lib/support-enabled.js";
import { useInstallPrompt } from "./hooks/use-install-prompt.js";
import { useJourneyProgress } from "./hooks/use-journey-progress.js";
import { useAccessSnapshot } from "./hooks/use-viewer-role.js";
import { BrandDialog } from "./brand-dialog.js";
import { PlatformSearch } from "./platform-search.js";
import { CHANGELOG_PAGE_URL } from "./changelog-service.js";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { openConsentPreferences } from "./lib/consent.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuGroup,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function useTranslatedNavLabel(label: string): string {
  const { t } = useTranslation();
  const key = navTranslationKey(label);
  return key ? t(key) : label;
}

// Dá um "pulse" no ícone quando o item vira ativo. A key remonta o motion.span a cada
// troca de estado, então o overshoot da mola dispara uma única vez na seleção (não a cada render).
function PulseIcon({ icon: Icon, active }: { icon: LucideIcon; active: boolean }) {
  return (
    <motion.span
      key={active ? "on" : "off"}
      className="relative z-10 flex"
      initial={{ scale: active ? 0.92 : 1 }}
      animate={{ scale: 1 }}
      transition={
        active ? { type: "spring", stiffness: 210, damping: 26, mass: 0.7 } : { duration: 0 }
      }
    >
      <Icon />
    </motion.span>
  );
}

function NavLinkItem({
  to,
  label,
  icon: Icon,
  active,
  badge,
}: {
  to: LinkProps["to"];
  label: string;
  icon: LucideIcon;
  active: boolean;
  // Contador do tipo "1/7" à direita do rótulo (hoje só a jornada usa).
  badge?: string;
}) {
  const translatedLabel = useTranslatedNavLabel(label);
  const { t } = useTranslation();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={<Link to={to} />}
        isActive={active}
        tooltip={badge ? `${translatedLabel} (${badge})` : translatedLabel}
        className="relative h-10 cursor-pointer overflow-visible data-active:bg-transparent data-active:text-primary"
      >
        <PulseIcon icon={Icon} active={active} />
        <span className="relative z-10 truncate">{translatedLabel}</span>
        {badge ? (
          // Some junto com o rótulo no modo de ícones: no trilho estreito não
          // cabe, e o tooltip já carrega o contador.
          //
          // `aria-label` num `<span>` sem role é IGNORADO pela ARIA (elemento
          // `generic`) — o dígito visível "1/7" era tudo que leitor de tela
          // lia. Padrão que funciona: dígito com `aria-hidden`, mais um
          // `sr-only` com o texto por extenso ao lado.
          <span className="relative z-10 ml-auto shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none tabular-nums text-primary-foreground group-data-[collapsible=icon]:hidden">
            <span aria-hidden="true">{badge}</span>
            <span className="sr-only">
              {t("navigation.startHere")}: {badge}
            </span>
          </span>
        ) : null}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function ExternalNavItem({
  href,
  label,
  icon: Icon,
}: {
  href: string;
  label: string;
  icon: LucideIcon;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={<a href={href} target="_blank" rel="noreferrer" />}
        tooltip={label}
        className="h-10 cursor-pointer"
      >
        <Icon />
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function NavGroupItem({
  group,
  pathname,
  expanded,
  onOpenChange,
}: {
  group: NavGroupDef;
  pathname: string;
  expanded: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const translatedGroupLabel = useTranslatedNavLabel(group.label);
  const activeTo = activeSubItem(group.items, pathname);
  const { isMobile, open, setOpen } = useSidebar();

  function handleGroupClick() {
    // Em modo de ícones o conteúdo do grupo fica oculto pelo CSS. Abrir apenas
    // o Collapsible não revela nada, então expandimos primeiro a sidebar para
    // que o clique tenha um resultado visível e os subitens possam ser usados.
    if (!isMobile && !open) setOpen(true);
  }

  return (
    <Collapsible
      render={<SidebarMenuItem />}
      open={expanded}
      onOpenChange={onOpenChange}
      className="group/collapsible"
    >
      <CollapsibleTrigger
        render={
          <SidebarMenuButton
            tooltip={translatedGroupLabel}
            className="h-10 cursor-pointer"
            onClick={handleGroupClick}
          />
        }
      >
        <group.icon />
        <span>{translatedGroupLabel}</span>
        <ChevronRight className="ml-auto transition-transform group-data-[open]/collapsible:rotate-90" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <SidebarMenuSub>
          {group.items.map((sub) => {
            const subActive = sub.to === activeTo;
            const key = navTranslationKey(sub.label);
            return (
              <SidebarMenuSubItem key={sub.to}>
                <SidebarMenuSubButton
                  render={<Link to={sub.to} />}
                  isActive={subActive}
                  className="relative h-10 cursor-pointer overflow-visible data-active:bg-transparent data-active:text-primary"
                >
                  <span className="relative z-10">
                    {key ? (
                      <TranslatedNavLabel translationKey={key} fallback={sub.label} />
                    ) : (
                      sub.label
                    )}
                  </span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            );
          })}
        </SidebarMenuSub>
      </CollapsibleContent>
    </Collapsible>
  );
}

function TranslatedNavLabel({
  translationKey,
  fallback,
}: {
  translationKey: string;
  fallback: string;
}) {
  const { t } = useTranslation();
  return t(translationKey, { defaultValue: fallback });
}

// Item do menu que abre o dialog de troca de marca. Mostra a marca ativa. O
// dialog em si é renderizado no SidebarUser (fora do dropdown), senão o Radix o
// desmontaria ao fechar o menu.
function OrgSwitcherItem({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  const { data: activeOrg } = useActiveOrganization();
  const activeOrgName = activeOrg?.name ?? null;

  return (
    <DropdownMenuItem
      onClick={(e) => {
        // Impede o fechamento imediato do menu engolir a abertura do dialog.
        e.preventDefault();
        onOpen();
      }}
    >
      <Building2 />
      <span className={`flex-1 truncate${activeOrgName ? "" : " text-muted-foreground"}`}>
        {activeOrgName ?? t("common.selectBrand")}
      </span>
      <ChevronRight className="ml-auto size-4 text-muted-foreground" />
    </DropdownMenuItem>
  );
}

// Submenu de tema (claro/escuro), persistido no store de preferências.
function ThemeSubmenu() {
  const { t } = useTranslation();
  const theme = usePreferences((s) => s.theme);
  const setTheme = usePreferences((s) => s.setTheme);
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        {theme === "dark" ? <Moon /> : <Sun />}
        <span className="flex-1">{t("common.theme")}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
          <DropdownMenuRadioItem value="light">{t("common.themes.light")}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">{t("common.themes.dark")}</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

// Rodapé reativo à sessão (Better Auth). Logado: avatar + dropdown com Perfil,
// Organização, Tema e Sair. Deslogado: "Entrar".
function SidebarUser() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();
  const user = session?.user;
  const [brandsOpen, setBrandsOpen] = useState(false);
  const { isStandalone } = useInstallPrompt();

  const primary = user ? user.name || user.email : t("common.unauthenticated");
  const secondary = user
    ? user.email
    : isPending
      ? t("common.checkingSession")
      : t("common.clickToSignIn");
  const initials = (user ? user.name || user.email || "?" : "—").slice(0, 2).toUpperCase();

  async function handleSignOut() {
    await logout();
    void navigate({ to: "/auth" });
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger render={<SidebarMenuButton size="lg" />}>
            <Avatar className="size-7 rounded-md">
              {user?.image ? <AvatarImage src={user.image} alt={`Avatar de ${primary}`} /> : null}
              <AvatarFallback className="rounded-md text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex flex-1 flex-col text-left leading-tight">
              <span className="text-sm">{primary}</span>
              <span className="truncate text-xs text-muted-foreground">{secondary}</span>
            </div>
            <ChevronsUpDown className="ml-auto size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-(--anchor-width) min-w-56">
            <DropdownMenuGroup>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col leading-tight">
                  <span className="text-sm">{primary}</span>
                  <span className="truncate text-xs text-muted-foreground">{secondary}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {user ? (
                <>
                  <DropdownMenuItem
                    render={<Link to="/profile" />}
                    nativeButton={false}
                    role="link"
                  >
                    <UserRound />
                    {t("common.profile")}
                  </DropdownMenuItem>
                  <OrgSwitcherItem onOpen={() => setBrandsOpen(true)} />
                  {/* Some quando o app já roda instalado — mesma regra do convite
                    em /profile e da linha no drawer do mobile. */}
                  {isStandalone ? null : (
                    <DropdownMenuItem
                      render={<Link to="/install" />}
                      nativeButton={false}
                      role="link"
                    >
                      <Download />
                      {t("common.installApp")}
                    </DropdownMenuItem>
                  )}
                  <ThemeSubmenu />
                  <LanguageSubmenu />
                  <DropdownMenuItem onClick={openConsentPreferences}>
                    <Cookie />
                    {t("cookieConsent.preferences")}
                  </DropdownMenuItem>
                  {/* "Configurações" saiu daqui: virou grupo próprio na navegação
                    (CONFIGURACOES), com uma rota por assunto. */}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut />
                    {t("common.signOut")}
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem render={<Link to="/auth" />} nativeButton={false} role="link">
                  <LogIn />
                  {t("common.signIn")}
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        {user ? <BrandDialog open={brandsOpen} onOpenChange={setBrandsOpen} /> : null}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AppSidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [searchOpen, setSearchOpen] = useState(false);
  const access = useAccessSnapshot();
  // O grupo "Click to call" só aparece com o recurso habilitado na org; a tela
  // que o habilita fica sempre em Integrações.
  const clickToCallEnabled = useClickToCallEnabled(
    canAccess({ ...access, path: "/management/click-to-call-audit" }),
  );
  const clickToCall = clickToCallNavGroup(clickToCallEnabled);
  // "Discagem manual" (/phone) idem: só entra no menu com o toggle da marca
  // ligado em Configurações › Discador. Sem gate de papel — o teclado é do
  // vendedor, e o back abre a leitura do toggle a qualquer membro.
  const manualDialEnabled = useManualDialEnabled();
  const discagemManual = showInNav(access, "/phone") ? manualDialNavLink(manualDialEnabled) : null;
  // "Meus chamados" só existe no menu quando o back tem as envs do Jira
  // configuradas (GET /v1/support/config). Sem isso o item ofereceria uma
  // tela que só dá 503 — a mesma regra que help-screen.tsx e
  // assistant-screen.tsx já aplicam ao botão "Abrir chamado".
  const supportEnabled = useSupportEnabled();
  // Jornada de primeiro acesso: enquanto sobrar passo acionável, o item sai do
  // fim da lista e vira o PRIMEIRO, com o contador. Concluída (ou sem dado
  // ainda), volta a ser um item comum lá embaixo.
  const journey = useJourneyProgress();
  const jornadaPendente = !!journey?.pendente;
  // Grupo Suporte: com o suporte desligado (sem envs do Jira no back), some
  // só "Meus chamados" — a Ajuda (FAQ + assistente) existe sem Jira.
  const grupoSuporte = {
    ...SUPORTE,
    items: supportEnabled
      ? SUPORTE.items
      : SUPORTE.items.filter((i) => i.to !== "/support/tickets"),
  };
  // O vendedor não vê a jornada (nem destacada, nem no fim da lista).
  const jornadaVisivel = showInNav(access, JORNADA_LINK.to as string);
  const jornadaItem = jornadaVisivel ? (
    <NavLinkItem
      to={JORNADA_LINK.to}
      label={JORNADA_LINK.label}
      icon={JORNADA_LINK.icon}
      active={pathname.startsWith("/journey")}
      badge={jornadaPendente && journey ? `${journey.done}/${journey.total}` : undefined}
    />
  ) : null;

  const visibleGroups = [
    MONITORAMENTO,
    RELATORIOS,
    AUDITORIA,
    GESTAO,
    FILA,
    FINANCEIRO,
    ...(clickToCall ? [clickToCall] : []),
    INTEGRACOES,
    CONFIGURACOES,
    grupoSuporte,
  ]
    .map((group) => filterNavGroup(group, access))
    .filter((group): group is NavGroupDef => group !== null);

  // Até a primeira interação, revela o grupo da rota atual, inclusive quando
  // as permissões terminam de carregar. null permite fechar todos os grupos.
  const [openGroup, setOpenGroup] = useState<string | null | undefined>(undefined);
  const expandedGroup =
    openGroup === undefined
      ? visibleGroups.find((group) => activeSubItem(group.items, pathname) !== null)?.label
      : openGroup;

  const searchDirectItems = [
    ...(jornadaVisivel ? [JORNADA_LINK] : []),
    ...(showInNav(access, "/dashboard")
      ? [{ to: "/dashboard" as const, label: "Dashboard", icon: LayoutDashboard }]
      : []),
    ...(canAccess({ ...access, path: "/dialer" })
      ? [{ to: "/dialer" as const, label: "Discador automático", icon: PhoneOutgoing }]
      : []),
    ...(showInNav(access, MEU_DESEMPENHO_LINK.to as string) ? [MEU_DESEMPENHO_LINK] : []),
    ...(discagemManual ? [discagemManual] : []),
  ];

  useEffect(() => {
    function handleShortcut(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    }

    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, []);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-2 py-3">
        <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center">
          {/* Logo + wordmark somem juntos no modo ícone: sobra só o trigger,
              que é o único alvo clicável de uma sidebar de 3rem. */}
          <AppBrandMark className="group-data-[collapsible=icon]:hidden" />
          <SidebarTrigger className="size-8 shrink-0" />
        </div>
        <SidebarMenu className="mt-2">
          <SidebarMenuItem>
            <SidebarMenuButton
              type="button"
              variant="outline"
              tooltip={t("platformSearch.trigger")}
              className="h-9 cursor-pointer bg-background/70 shadow-xs"
              onClick={() => setSearchOpen(true)}
            >
              <Search aria-hidden="true" />
              <span>{t("platformSearch.trigger")}</span>
              <Kbd
                aria-hidden="true"
                className="ml-auto bg-background group-data-[collapsible=icon]:hidden"
              >
                Ctrl K
              </Kbd>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <LayoutGroup>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1.5">
                {jornadaPendente && jornadaItem}
                {showInNav(access, "/dashboard") && (
                  <NavLinkItem
                    to="/dashboard"
                    label={t("navigation.dashboard")}
                    icon={LayoutDashboard}
                    active={pathname.startsWith("/dashboard")}
                  />
                )}
                {canAccess({ ...access, path: "/dialer" }) && (
                  <NavLinkItem
                    to="/dialer"
                    label={t("navigation.automaticDialer")}
                    icon={PhoneOutgoing}
                    active={pathname.startsWith("/dialer")}
                  />
                )}
                {showInNav(access, MEU_DESEMPENHO_LINK.to as string) && (
                  <NavLinkItem
                    to={MEU_DESEMPENHO_LINK.to}
                    label={t("navigation.myPerformance")}
                    icon={MEU_DESEMPENHO_LINK.icon}
                    active={pathname.startsWith("/my-performance")}
                  />
                )}
                {discagemManual && (
                  <NavLinkItem
                    to={discagemManual.to}
                    label={discagemManual.label}
                    icon={discagemManual.icon}
                    active={pathname.startsWith("/phone")}
                  />
                )}
                {visibleGroups.map((group) => (
                  <NavGroupItem
                    key={group.label}
                    group={group}
                    pathname={pathname}
                    expanded={expandedGroup === group.label}
                    onOpenChange={(open) => setOpenGroup(open ? group.label : null)}
                  />
                ))}
                {!jornadaPendente && jornadaItem}
                <ExternalNavItem
                  href={CHANGELOG_PAGE_URL}
                  label={t("navigation.whatsNew")}
                  icon={Sparkles}
                />
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </LayoutGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarUser />
      </SidebarFooter>
      <PlatformSearch
        open={searchOpen}
        onOpenChange={setSearchOpen}
        directItems={searchDirectItems}
        groups={visibleGroups}
        onSelect={(to) => void navigate({ to })}
      />
    </Sidebar>
  );
}
